import React from 'react';
import Button from '../../../../Abstracts/Button/Button';

// @ts-ignore
import styles from './AddToCalendar.module.scss?module';

const EVENT_TITLE = 'NaskorSports Newcomer Week';
const EVENT_URL = 'https://naskorsports.com/newcomer-week';
const EVENT_DESCRIPTION =
  'Newcomer Week at NaskorSports: discover our newly listed brands and ' +
  'products, exclusive limited-time discounts and free samples. ' +
  EVENT_URL;

/**
 * The NCW event period comes from the NCW configuration (API
 * /api/onlineshop/newcomer-week/current-ncw), so the calendar entry follows the
 * configured event and does not have to be maintained in the code.
 *
 * `start`/`end` are ISO strings (DateTime::ATOM) and describe the first and the
 * last day of the event (both inclusive). All-day calendar entries end
 * exclusive, therefore every "end" below is the last day + 1 day.
 */
export interface AddToCalendarProps {
  start: string | null;
  end: string | null;
  className?: string;
}

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Reads the calendar day out of an ISO string without letting the browser time
 * zone shift it (`2026-09-14T00:00:00+02:00` must stay the 14th, also for a
 * visitor in a negative UTC offset).
 */
const parseIsoDay = (iso: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) {
    return null;
  }
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
};

const addDays = (date: Date, days: number): Date =>
  new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

/** 20260914 */
const asBasicDate = (date: Date): string =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate()
  )}`;

/** 2026-09-14 */
const asIsoDate = (date: Date): string =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}`;

const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');

const buildIcs = (firstDay: Date, dayAfterLastDay: Date): string => {
  const stamp = `${asBasicDate(firstDay)}T080000Z`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NaskorSports//Newcomer Week//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:newcomer-week-${asBasicDate(firstDay)}@naskorsports.com`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${asBasicDate(firstDay)}`,
    `DTEND;VALUE=DATE:${asBasicDate(dayAfterLastDay)}`,
    'TRANSP:TRANSPARENT',
    `SUMMARY:${escapeIcsText(EVENT_TITLE)}`,
    `DESCRIPTION:${escapeIcsText(EVENT_DESCRIPTION)}`,
    `URL:${EVENT_URL}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeIcsText(EVENT_TITLE)}`,
    'TRIGGER:-P1D',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

/**
 * "Add to calendar" for the pre-event landing page.
 *
 * The button downloads an .ics file (Apple Calendar and the Outlook desktop
 * client), the two links below open the prefilled event in Google Calendar and
 * in Outlook on the web.
 */
export default function AddToCalendar({
  start,
  end,
  className,
}: AddToCalendarProps) {
  const firstDay = start ? parseIsoDay(start) : null;
  const lastDay = end ? parseIsoDay(end) : null;

  if (!firstDay || !lastDay) {
    // No configured event period -> no calendar entry with a wrong date.
    return null;
  }

  const dayAfterLastDay = addDays(lastDay, 1);

  const downloadIcs = () => {
    const blob = new Blob([buildIcs(firstDay, dayAfterLastDay)], {
      type: 'text/calendar;charset=utf-8',
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'naskorsports-newcomer-week.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const googleUrl =
    'https://calendar.google.com/calendar/render?action=TEMPLATE' +
    `&text=${encodeURIComponent(EVENT_TITLE)}` +
    `&dates=${asBasicDate(firstDay)}/${asBasicDate(dayAfterLastDay)}` +
    `&details=${encodeURIComponent(EVENT_DESCRIPTION)}`;

  const outlookUrl =
    'https://outlook.office.com/calendar/0/deeplink/compose' +
    '?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&allday=true' +
    `&startdt=${asIsoDate(firstDay)}` +
    `&enddt=${asIsoDate(dayAfterLastDay)}` +
    `&subject=${encodeURIComponent(EVENT_TITLE)}` +
    `&body=${encodeURIComponent(EVENT_DESCRIPTION)}`;

  return (
    <div className={className}>
      <Button className={styles.AddToCalendarButton} onClick={downloadIcs}>
        Add to calendar
      </Button>
      <div className={styles.CalendarLinks}>
        <a href={googleUrl} target="_blank" rel="noopener noreferrer">
          Google Calendar
        </a>
        <span aria-hidden="true">|</span>
        <a href={outlookUrl} target="_blank" rel="noopener noreferrer">
          Outlook
        </a>
      </div>
    </div>
  );
}
